import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AdminPageShell } from "@/components/shared/admin-page-shell";
import { Card, CardContent } from "@/components/ui/card";
import { TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AllowedOriginsForm } from "@/features/settings/components/AllowedOriginsForm";
import { EmailSettingsForm } from "@/features/settings/components/EmailSettingsForm";
import { MaintenanceModeForm } from "@/features/settings/components/MaintenanceModeForm";
import { PlatformSettingsForm } from "@/features/settings/components/PlatformSettingsForm";
import { SecuritySettingsForm } from "@/features/settings/components/SecuritySettingsForm";
import { settingsService } from "@/features/settings/services/settingsService";
import { queryKeys } from "@/lib/query-keys";

type SettingsTab = "platform" | "security" | "email" | "origins" | "maintenance";

const TABS: Array<{ key: SettingsTab; label: string }> = [
  { key: "platform", label: "المنصة" },
  { key: "security", label: "الأمان" },
  { key: "email", label: "البريد الإلكتروني" },
  { key: "origins", label: "الروابط المسموح بها" },
  { key: "maintenance", label: "وضع الصيانة" },
];

export default function SettingsPage() {
  const [tab, setTab] = useState<SettingsTab>("platform");

  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.settings.root(),
    queryFn: () => settingsService.get(),
  });

  return (
    <AdminPageShell
      title="إعدادات المنصة"
      description="الإعدادات العامة والأمان والبريد الإلكتروني والصيانة"
      isLoading={isLoading}
      error={error}
    >
      <TabsList className="mb-4">
        {TABS.map((t) => (
          <TabsTrigger
            key={t.key}
            data-state={tab === t.key ? "active" : "inactive"}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </TabsTrigger>
        ))}
      </TabsList>

      <Card>
        <CardContent className="pt-6">
          {tab === "platform" && (
            <PlatformSettingsForm platform={data?.platform} />
          )}
          {tab === "security" && (
            <SecuritySettingsForm security={data?.security} />
          )}
          {tab === "email" && <EmailSettingsForm emailFrom={data?.emailFrom} />}
          {tab === "origins" && (
            <AllowedOriginsForm allowedOrigins={data?.allowedOrigins} />
          )}
          {tab === "maintenance" && (
            <MaintenanceModeForm
              maintenanceMode={data?.maintenanceMode}
              maintenanceMessage={data?.maintenanceMessage}
            />
          )}
        </CardContent>
      </Card>
    </AdminPageShell>
  );
}
